import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

const ChatMessage = ({ 
  message, 
  index, 
  isLastBotMessage, 
  botMessageRef, 
  handleModelDiagnostics, 
  handleInterpretResults, 
  isSending, 
  downloadPlot 
}) => {
  const renderUserMessage = () => (
    <div className="user-message-content">
      {/* Display uploaded images for user messages */}
      {message.hasImages && message.images?.length > 0 && (
        <div className="message-images">
          {message.images.map((img, imgIndex) => (
            <div key={imgIndex} className="message-image-container">
              <img 
                src={img.url}                                 
                className="message-image"
                alt={`Uploaded image ${imgIndex + 1}`}
                onError={(e) => {
                  console.error('Image failed to load:', img);
                  console.error('Error:', e.target.error);
                }}
                onLoad={() => console.log('Image loaded successfully:', img.name)}
              />
            </div>
          ))}
        </div>
      )}
      {/* Display text if present */}
      {message.text && (
        <div className="message-text">
          {message.text}
        </div>
      )}
    </div>
  );

  const renderBotMessage = () => (
    <div>
      <ReactMarkdown
        remarkPlugins={[remarkMath]}
        rehypePlugins={[rehypeKatex]}
      >
        {message.text}
      </ReactMarkdown>
      
      {message.showActionButtons && (
        <div className="action-buttons-container">
          <button
            onClick={handleModelDiagnostics}
            disabled={isSending}
            className="action-button diagnostics-button"
          >
            Model Diagnostics
          </button>
          
          <button
            onClick={handleInterpretResults}
            disabled={isSending}
            className="action-button interpretation-button"
          >
            Model Interpretation
          </button>
        </div>
      )}
      
      {message.hasRegLine && message.regLineUrl && (
        <div className="plot-container">
          <h4 className="plot-header">Regression Line</h4>
          <div className="plot-content">
            <img 
              src={message.regLineUrl} 
              alt="Regression Line" 
              className="plot-image regression-line"
            />
            {message.regressionLineDownloadId && (
              <div className="download-button-container">
                <button
                  onClick={() => downloadPlot(message.regressionLineDownloadId, 'regression_line')}
                  className="download-button"
                >
                  <i className="fas fa-download"></i>
                </button>
                <span className="download-button-tooltip">Download Figure</span>
              </div>
            )}
          </div>
        </div>
      )}

      {message.hasPlot && message.plotUrl && (
        <div className="plot-container">
          <h4 className="plot-header">Residuals Plot</h4>
          <div className="plot-content">
            <img 
              src={message.plotUrl} 
              alt="Residuals Plot" 
              className="plot-image"
            />
            {message.residualsDownloadId && (
              <div className="download-button-container">
                <button
                  onClick={() => downloadPlot(message.residualsDownloadId, 'residuals')}
                  className="download-button"
                >
                  <i className="fas fa-download"></i>
                </button>
                <span className="download-button-tooltip">Download Figure</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {message.hasPreview && message.previewData && (
        <div className="csv-preview-container">
          <div className="csv-table-wrapper">
            <table className="csv-preview-table">
              <thead>
                <tr>
                  {message.previewData.columns.map((col, i) => (
                    <th key={i}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {message.previewData.preview.map((row, i) => (
                  <tr key={i}>
                    {message.previewData.columns.map((col, j) => (
                      <td key={j}>{row[col]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="csv-question">
            Which variable would you like to predict?
          </div>
          <div className="csv-columns-info">
            Available columns: {message.previewData.columns.join(', ')}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={isLastBotMessage ? botMessageRef : null}
      className={`chat-message ${
        message.sender === "user" ? "user-message" : "bot-message"
      } ${message.text === "•" ? "loading" : ""} ${message.isNewMessage ? "new-message" : ""}`}
    >
      {message.sender === "user" ? (
        renderUserMessage()
      ) : (
        message.sender === "bot" && message.text !== "•" ? (
          renderBotMessage()
        ) : (
          message.text
        )
      )}
    </div>
  );
};

export default ChatMessage;
